import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { adminApi } from "../../../api/adminApi";

const ROL_SEVERITY = {
  admin: "danger",
  adminauto: "warning",
  agente: "info",
  user: "secondary",
};

// Roles que requieren tener un casino asignado
const ROLES_CON_CASINO = ["adminauto", "agente"];

export default function PanelUsuarios() {
  const toast = useRef(null);
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [casinos, setCasinos] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [cargando, setCargando] = useState(true);
  const [guardandoRol, setGuardandoRol] = useState(null);
  const [guardandoCasino, setGuardandoCasino] = useState(null);

  useEffect(() => {
    cargarDatos();
  }, []);

  async function cargarDatos() {
    setCargando(true);
    try {
      const [usuRes, rolRes, casRes] = await Promise.all([
        adminApi.getUsuarios(),
        adminApi.getRoles(),
        adminApi.getCasinos({ soloActivos: true }),
      ]);
      setUsuarios(usuRes.data);
      setRoles(rolRes.data.map((r) => ({ label: r.nombre, value: r.id })));
      setCasinos([
        { label: "— Sin casino —", value: null },
        ...casRes.data.map((c) => ({ label: c.nombre, value: c.id })),
      ]);
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "No se pudieron cargar los datos" });
    } finally {
      setCargando(false);
    }
  }

  const usuariosFiltrados = usuarios.filter((u) => {
    const q = filtro.toLowerCase();
    return (
      u.email?.toLowerCase().includes(q) ||
      u.username?.toLowerCase().includes(q) ||
      u.nombre?.toLowerCase().includes(q) ||
      u.apellido?.toLowerCase().includes(q)
    );
  });

  function confirmarCambioRol(usuario, nuevoRol) {
    if (nuevoRol === usuario.rol) return;
    confirmDialog({
      message: `¿Cambiar el rol de ${usuario.email} de "${usuario.rol}" a "${nuevoRol}"?`,
      header: "Confirmar cambio de rol",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sí, cambiar",
      rejectLabel: "Cancelar",
      accept: () => cambiarRol(usuario, nuevoRol),
    });
  }

  async function cambiarRol(usuario, nuevoRol) {
    setGuardandoRol(usuario.id);
    try {
      await adminApi.cambiarRolUsuario(usuario.id, nuevoRol);
      setUsuarios((prev) =>
        prev.map((u) => (u.id === usuario.id ? { ...u, rol: nuevoRol } : u))
      );
      toast.current?.show({ severity: "success", summary: "Rol actualizado", detail: `${usuario.email} → ${nuevoRol}` });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "No se pudo actualizar el rol" });
    } finally {
      setGuardandoRol(null);
    }
  }

  async function cambiarCasino(usuario, nuevoCasinoId) {
    if (nuevoCasinoId === (usuario.casino || null)) return;
    setGuardandoCasino(usuario.id);
    try {
      await adminApi.cambiarCasinoUsuario(usuario.id, nuevoCasinoId);
      setUsuarios((prev) =>
        prev.map((u) => (u.id === usuario.id ? { ...u, casino: nuevoCasinoId } : u))
      );
      const nombreCasino = casinos.find((c) => c.value === nuevoCasinoId)?.label || "sin casino";
      toast.current?.show({ severity: "success", summary: "Casino actualizado", detail: `${usuario.email} → ${nombreCasino}` });
    } catch (e) {
      const msg = e?.response?.data?.error || "No se pudo actualizar el casino";
      toast.current?.show({ severity: "error", summary: "Error", detail: msg });
    } finally {
      setGuardandoCasino(null);
    }
  }

  const rolTemplate = (rowData) => (
    <Tag value={rowData.rol || "user"} severity={ROL_SEVERITY[rowData.rol] || "secondary"} />
  );

  const cambioRolTemplate = (rowData) => (
    <Dropdown
      value={rowData.rol || "user"}
      options={roles}
      onChange={(e) => confirmarCambioRol(rowData, e.value)}
      placeholder="Cambiar rol"
      disabled={guardandoRol === rowData.id}
      style={{ width: "170px" }}
    />
  );

  const casinoTemplate = (rowData) => {
    if (!ROLES_CON_CASINO.includes(rowData.rol)) {
      return <span style={{ color: "#bbb", fontSize: 13 }}>N/A</span>;
    }
    return (
      <Dropdown
        value={rowData.casino || null}
        options={casinos}
        onChange={(e) => cambiarCasino(rowData, e.value)}
        placeholder="Seleccionar"
        disabled={guardandoCasino === rowData.id}
        style={{ width: "200px" }}
        showClear={false}
      />
    );
  };

  const nombreTemplate = (rowData) => (
    <span>{[rowData.nombre, rowData.apellido].filter(Boolean).join(" ") || rowData.username || "—"}</span>
  );

  return (
    <div className="p-3">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">Usuarios</h2>
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Buscar por nombre o email..."
            style={{ width: "280px" }}
          />
        </span>
      </div>

      <DataTable
        value={usuariosFiltrados}
        loading={cargando}
        paginator
        rows={15}
        rowsPerPageOptions={[10, 15, 30, 50]}
        emptyMessage="No se encontraron usuarios"
        stripedRows
        size="small"
      >
        <Column field="email" header="Email" sortable style={{ minWidth: "220px" }} />
        <Column header="Nombre" body={nombreTemplate} sortable style={{ minWidth: "160px" }} />
        <Column header="Rol actual" body={rolTemplate} sortable field="rol" style={{ minWidth: "110px" }} />
        <Column header="Cambiar rol" body={cambioRolTemplate} style={{ minWidth: "190px" }} />
        <Column header="Casino" body={casinoTemplate} style={{ minWidth: "220px" }} />
      </DataTable>
    </div>
  );
}
